import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  BarChart3,
  Wallet,
  CreditCard,
  Clock,
  Building2,
  ArrowLeft
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import usePlatformFee from '@/hooks/usePlatformFee';

export default function FinancialDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // SOURCE OF TRUTH dinâmico para comissão (admin → Financeiro → Configurações)
  const { platformFeePercent } = usePlatformFee();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30days');
  const [metrics, setMetrics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [providersRanking, setProvidersRanking] = useState([]);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchAllData();
  }, [user, navigate, period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel with individual error handling
      const [metricsRes, chartRes, transactionsRes, summaryRes, rankingRes] = await Promise.allSettled([
        axios.get(`${API}/admin/financial-dashboard/metrics?period=${period}`, { headers }),
        axios.get(`${API}/admin/financial-dashboard/revenue-chart?period=${period}`, { headers }),
        axios.get(`${API}/admin/financial-dashboard/transactions?limit=15`, { headers }),
        axios.get(`${API}/admin/financial-dashboard/summary`, { headers }),
        axios.get(`${API}/admin/financial-dashboard/providers-ranking?period=${period}&limit=5`, { headers })
      ]);

      // Handle metrics
      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data.metrics);
      } else {
        setMetrics(null);
      }

      // Handle chart data
      if (chartRes.status === 'fulfilled') {
        setChartData(chartRes.value.data.data || []);
      } else {
        setChartData([]);
      }

      // Handle transactions
      if (transactionsRes.status === 'fulfilled') {
        setTransactions(transactionsRes.value.data.transactions || []);
      } else {
        setTransactions([]);
      }

      // Handle summary
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data);
      } else {
        setSummary(null);
      }

      // Handle providers ranking
      if (rankingRes.status === 'fulfilled') {
        setProvidersRanking(rankingRes.value.data.ranking || []);
      } else {
        setProvidersRanking([]);
      }

    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      // Não mostrar toast de erro - apenas exibir dados vazios
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'commission': 'Comissão',
      'service_payment': 'Pagamento Serviço',
      'chargeback': 'Chargeback',
      'chargeback_reversal': 'Reversão Chargeback',
      'payout': 'Repasse',
      'refund': 'Reembolso'
    };
    return labels[type] || type;
  };

  const getTransactionColor = (type, direction) => {
    if (type === 'chargeback') return 'text-[#EF4444]';
    if (type === 'chargeback_reversal') return 'text-[#16A34A]';
    return direction === 'credit' ? 'text-[#16A34A]' : 'text-[#EF4444]';
  };

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/control-portal-clube/dashboard')}
                data-testid="back-to-dashboard-btn"
                className="hover:bg-[#2563EB]/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2 text-[#0B1F3A]">
                  <BarChart3 className="w-6 h-6 text-[#2563EB]" />
                  Dashboard Financeiro
                </h1>
                <p className="text-sm text-slate-500">
                  Visão geral das métricas financeiras da plataforma
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[150px] border-[#E2E8F0]" data-testid="period-filter">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="7days">7 dias</SelectItem>
                  <SelectItem value="30days">30 dias</SelectItem>
                  <SelectItem value="90days">90 dias</SelectItem>
                  <SelectItem value="year">Este ano</SelectItem>
                  <SelectItem value="all">Todo período</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={fetchAllData}
                disabled={loading}
                data-testid="refresh-btn"
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* GMV */}
          <Card className="border-[#E2E8F0]" data-testid="gmv-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.gmv?.label || 'Volume Total de Serviços (GMV)'}
              </CardTitle>
              <div className="p-2 bg-[#2563EB]/10 rounded-lg">
                <DollarSign className="w-4 h-4 text-[#2563EB]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#0B1F3A]">{formatCurrency(metrics?.gmv?.value)}</div>
              <p className="text-xs text-slate-500 mt-1">
                {metrics?.gmv?.count || 0} serviços concluídos
              </p>
            </CardContent>
          </Card>

          {/* Platform Revenue */}
          <Card className="border-[#E2E8F0]" data-testid="revenue-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.platform_revenue?.label || 'Receita da Plataforma'}
              </CardTitle>
              <div className="p-2 bg-[#16A34A]/10 rounded-lg">
                <TrendingUp className="w-4 h-4 text-[#16A34A]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#16A34A]">
                {formatCurrency(metrics?.platform_revenue?.value)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {metrics?.platform_revenue?.percentage != null
                  ? metrics.platform_revenue.percentage
                  : platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}% do GMV
              </p>
            </CardContent>
          </Card>

          {/* Provider Payout */}
          <Card className="border-[#E2E8F0]" data-testid="payout-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.provider_payout?.label || 'Valor Repassado aos Prestadores'}
              </CardTitle>
              <div className="p-2 bg-[#F97316]/10 rounded-lg">
                <Users className="w-4 h-4 text-[#F97316]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#F97316]">
                {formatCurrency(metrics?.provider_payout?.value)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {metrics?.provider_payout?.percentage || 75}% do GMV
              </p>
            </CardContent>
          </Card>

          {/* Wallet Balance */}
          <Card className="border-[#E2E8F0]" data-testid="wallet-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.wallet_balance?.label || 'Saldo da Plataforma'}
              </CardTitle>
              <div className="p-2 bg-[#2563EB]/10 rounded-lg">
                <Wallet className="w-4 h-4 text-[#2563EB]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#0B1F3A]">{formatCurrency(metrics?.wallet_balance?.value)}</div>
              {metrics?.wallet_balance?.pending > 0 && (
                <p className="text-xs text-[#F97316] mt-1">
                  + {formatCurrency(metrics?.wallet_balance?.pending)} pendente
                </p>
              )}
            </CardContent>
          </Card>

          {/* Chargebacks */}
          <Card className="border-[#E2E8F0]" data-testid="chargeback-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.chargebacks?.label || 'Chargebacks em Aberto'}
              </CardTitle>
              <div className="p-2 bg-[#EF4444]/10 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${metrics?.chargebacks?.value > 0 ? 'text-[#EF4444]' : 'text-[#0B1F3A]'}`}>
                {formatCurrency(metrics?.chargebacks?.value)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {metrics?.chargebacks?.count || 0} disputas abertas
              </p>
            </CardContent>
          </Card>

          {/* Pending Payout */}
          <Card className="border-[#E2E8F0]" data-testid="pending-payout-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {metrics?.pending_payout?.label || 'Repasse Pendente'}
              </CardTitle>
              <div className="p-2 bg-[#F97316]/10 rounded-lg">
                <Clock className="w-4 h-4 text-[#F97316]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#F97316]">
                {formatCurrency(metrics?.pending_payout?.value)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {metrics?.pending_payout?.count || 0} solicitações pendentes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Chart and Ranking */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2 border-[#E2E8F0]" data-testid="revenue-chart">
            <CardHeader>
              <CardTitle className="text-lg text-[#0B1F3A]">Faturamento por Período</CardTitle>
              <CardDescription className="text-slate-500">
                GMV e receita da plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(v) => `R$${v}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => `Data: ${label}`}
                      contentStyle={{ borderRadius: '8px', borderColor: '#E2E8F0' }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="gmv"
                      name="GMV"
                      stroke="#2563EB"
                      fill="#2563EB30"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="platform_revenue"
                      name="Receita Plataforma"
                      stroke="#16A34A"
                      fill="#16A34A30"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Sem dados para o período selecionado
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Providers */}
          <Card data-testid="providers-ranking">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Top Prestadores
              </CardTitle>
              <CardDescription>
                Ranking por faturamento
              </CardDescription>
            </CardHeader>
            <CardContent>
              {providersRanking.length > 0 ? (
                <div className="space-y-3">
                  {providersRanking.map((provider, idx) => (
                    <div 
                      key={provider.provider_id}
                      className="flex items-center justify-between p-2 rounded-lg bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-500 text-white' :
                          idx === 1 ? 'bg-slate-400 text-white' :
                          idx === 2 ? 'bg-amber-700 text-white' :
                          'bg-slate-200 text-slate-600'
                        }`}>
                          {provider.position}
                        </span>
                        <div>
                          <p className="text-sm font-medium truncate max-w-[120px]">
                            {provider.provider_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {provider.total_services} serviços
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-green-600">
                        {formatCurrency(provider.total_revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  Sem dados disponíveis
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transactions Table */}
        <Card data-testid="transactions-table">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Atividade Financeira Recente
            </CardTitle>
            <CardDescription>
              Últimas transações do ledger da plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Referência</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-sm">
                        {formatDate(tx.date)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getTransactionTypeLabel(tx.type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">
                        {tx.description || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {tx.reference_type}: {tx.reference_id?.substring(0, 8)}...
                      </TableCell>
                      <TableCell className={`text-right font-medium ${getTransactionColor(tx.type, tx.direction)}`}>
                        {tx.direction === 'credit' ? '+' : '-'}
                        {formatCurrency(tx.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Nenhuma transação encontrada
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {summary && (
          <Card data-testid="summary-card">
            <CardHeader>
              <CardTitle className="text-lg">Resumo Geral</CardTitle>
              <CardDescription>Estatísticas desde o início da plataforma</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.total_services}</p>
                  <p className="text-xs text-muted-foreground">Serviços Totais</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_gmv)}</p>
                  <p className="text-xs text-muted-foreground">GMV Total</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.platform_revenue_all_time)}</p>
                  <p className="text-xs text-muted-foreground">Receita Total</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.active_providers}</p>
                  <p className="text-xs text-muted-foreground">Prestadores Ativos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.active_clients}</p>
                  <p className="text-xs text-muted-foreground">Clientes Ativos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.active_subscriptions}</p>
                  <p className="text-xs text-muted-foreground">Assinaturas Ativas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
